// Bearer links: whoever holds the URL gets in, so the token must not reach
// analytics, replays or error reports. Path tokens are cuid2, base64url or
// alphanumeric ids, hence `[\w-]+`. `/access?return-to=` carries /connect and
// /link-c URLs URL-encoded, so `/`, `?`, `&` and `=` also match as %2F, %3F,
// %26 and %3D.
const SLASH = "(?:/|%2F)";
// Pages a patient or buyer opens from a bearer link.
const TOKEN_PAGES = "pa|measure-session";
// `(?!convert…)`: in /api/file-access/convert, `convert` is a route, not a
// token.
const TOKEN_SEGMENT = new RegExp(
  `(${SLASH}(?:${TOKEN_PAGES}|link-c|patient-link|file-access(?:${SLASH}convert)?|internal${SLASH}sessions|reset-password)${SLASH})(?!convert(?![\\w-]))[\\w-]+`,
  "gi",
);
// The password reset page, opened from an email, has its token in the query.
const TOKEN_PAGE = new RegExp(
  `^(?:/[a-z]{2})?/(?:(?:${TOKEN_PAGES})/[\\w-]+|access/email-forget-callback(?:/|$))`,
);

// `token`: /api/photo, better-auth reset and verify-email links. `state` and
// `code`: OAuth (/connect, social sign-in callbacks).
const TOKEN_PARAM = /((?:^|[?&])(?:token|state|code)=)[^&#\s"'<>]+/gi;
const ENCODED_TOKEN_PARAM =
  /((?:%3F|%26)(?:token|state|code)%3D)(?:(?!%26|%23)[^&#\s"'<>])+/gi;

// Customer photos are public-read S3 objects: the object URL is the photo.
const S3_OBJECT_KEY = /([./]s3(?:[.-][\w-]+)?\.amazonaws\.com\/)[^?#\s"'<>]+/gi;

/**
 * Replaces bearer tokens in a URL, path, query string or free text with
 * `:token`. Regex-only, so malformed URLs pass through instead of throwing.
 */
export function redactTokens(value: string) {
  return value
    .replace(TOKEN_SEGMENT, "$1:token")
    .replace(TOKEN_PARAM, "$1:token")
    .replace(ENCODED_TOKEN_PARAM, "$1:token")
    .replace(S3_OBJECT_KEY, "$1:token");
}

/**
 * Whether a pathname, with or without the locale prefix, is a page opened from
 * a bearer link: a patient or buyer link, or a password reset email.
 */
export function isTokenPage(pathname: string) {
  return TOKEN_PAGE.test(pathname);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * `redactTokens` over every string, and every key, in plain objects and
 * arrays. Returns copies; anything else passes through. Breadcrumb data is
 * raw console arguments, so a cycle becomes Sentry's own `[Circular ~]`.
 */
export function redactTokensDeep<T>(value: T): T {
  const ancestors = new WeakSet<object>();
  const walk = (item: unknown): unknown => {
    if (typeof item === "string") return redactTokens(item);
    if (!Array.isArray(item) && !isPlainObject(item)) return item;
    if (ancestors.has(item)) return "[Circular ~]";
    ancestors.add(item);
    let redacted: unknown[] | Record<string, unknown>;
    if (Array.isArray(item)) {
      redacted = item.map(walk);
    } else {
      redacted = {};
      for (const [key, child] of Object.entries(item)) {
        redacted[redactTokens(key)] = walk(child);
      }
    }
    ancestors.delete(item);
    return redacted;
  };
  return walk(value) as T;
}
